"use client";

import { useEffect, useState } from "react";
import { Trash2, View } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useRequestStore } from "../../data/stores/loggerStore";
import { apiCall, useApiCall } from "../../data/useFetcher";
import moment from "moment";
import PrimaryBtn, { LoadMore, MainPaginate } from "../partials/btn";
import { CheckAllboxes, Checkbox } from "./SystemInformArea";
import { TriggerModal } from "../modals/successmodal";
import { Status2 as Status } from "../../utils/types";
// import { title } from "process";

interface Contact {
  id: number;
  company: string;
  focalPerson: string;
  email: string;
  phone: string;
  status: Status;
  date: string;
}

function RequestTable({
  usecase,
  statusText,
  search,
}: {
  usecase?: string;
  statusText?: string;
  search?: string;
}) {
  const [datum, setDatum] = useState<any>({
      docs: [],
      hasNextPage: false,
    }),
    [updater, setUpdater] = useState<any>(null),
    { getDynamicLogger, getSearchLogger } = useRequestStore(),
    { onSubmitAPI, loading: isLoading } = useApiCall();

  const onSubmit = async () => {
    await onSubmitAPI({
      url: `/api/v1/auth/recommendation/remove-account`,
      type: "put",
      data: {
        recommendations: updater?.data?.map((it) => it?._id),
        type: updater?.isOpen,
      },
      toggleModal: () => {
        apiCall({
          type: "get",
          url: `/api/v1/auth/recommendation?privilege=system owner${
            usecase && statusText
              ? `&statusText=${statusText}`
              : usecase === "newly-uploaded"
              ? `&streamline=week`
              : ""
          }${search ? `&search=${search}` : ""}`,
          getter: (d: any) =>
            search
              ? getSearchLogger(d)
              : getDynamicLogger(
                  d,
                  usecase === "newly-uploaded" ? "newlyUploadedData" : "allData"
                ),
        });
        setUpdater(null);
      },
    });
  };

  return (
    <div className="px-5">
      {datum?.docs?.some?.((ic) => ic?.isChecked) && (
        <div className="flex justify-end items-center gap-3 my-4">
          <PrimaryBtn
            loadingColor="red"
            text="Delete"
            className={
              "w-full text-left p-3 hover:bg-gray-100 text-red-500 rounded-md flex items-center gap-3 border border-dashed border-red-500 relative"
            }
            onClick={() =>
              setUpdater({
                isOpen: "delete",
                data: datum?.docs?.filter((iz) => iz?.isChecked),
                title: "Delete Request",
              })
            }
          />
        </div>
      )}
      <table className="w-full">
        <thead>
          <tr className="">
            <th className="p-3 text-left font-normal text-gray-600">
              <CheckAllboxes datum={datum} setDatum={setDatum} />
            </th>
            <th className="p-3 text-xs text-left font-medium text-[#667085]">
              Company
            </th>
            <th className="p-3 text-xs text-left font-medium text-[#667085] hidden">
              Focal Person
            </th>
            <th className="p-3 text-xs text-left font-medium text-[#667085]">
              Email address
            </th>
            <th className="p-3 text-xs text-left font-medium text-[#667085] hidden">
              Phone
            </th>
            <th className="p-3 text-xs text-left font-medium text-[#667085]">
              Status <span className="inline-block ml-1">↓</span>
            </th>
            <th className="p-3 text-xs text-left font-medium text-[#667085]">
              Date
            </th>
            <th className="p-3 text-xs text-left font-medium text-[#667085]"></th>
          </tr>
        </thead>
        <InviteRequestTable
          usecase={usecase}
          datum={datum}
          setDatum={setDatum}
          updater={updater}
          setUpdater={setUpdater}
          onSubmit={onSubmit}
          isLoading={isLoading}
          statusText={statusText}
          search={search}
        />
      </table>
    </div>
  );
}

export default RequestTable;

export const InviteRequestTable = ({
  usecase,
  datum,
  setDatum,
  updater,
  setUpdater,
  onSubmit,
  isLoading,
  statusText,
  search,
}: {
  usecase?: string;
  datum: any;
  setDatum: React.Dispatch<any>;
  updater: any;
  setUpdater: React.Dispatch<any>;
  onSubmit: () => Promise<void>;
  isLoading: string | boolean;
  statusText?: string;
  search?: string;
}) => {
  const { mainSearch, isFound, newlyUploadedData, allData }: any =
      useRequestStore(),
    { getDynamicLogger, resetLoggerSearch, getSearch, getSearchLogger } =
      useRequestStore(),
    [loading, setLoading] = useState<string | boolean>(false),
    navigate = useNavigate();

  useEffect(() => {
    resetLoggerSearch();
  }, []);

  useEffect(() => {
    if (isFound) setDatum(mainSearch);
    else setDatum(usecase === "newly-uploaded" ? newlyUploadedData : allData);
  }, [isFound, mainSearch, newlyUploadedData, usecase, allData]);

  useEffect(() => {
    if (search) getSearch(search);
    apiCall({
      type: "get",
      url: `/api/v1/auth/recommendation?a=a${
        usecase && statusText
          ? `&statusText=${statusText}`
          : usecase === "newly-uploaded"
          ? `&streamline=week`
          : ""
      }${search ? `&search=${search}` : ""}`,
      getter: (d: any) =>
        search
          ? getSearchLogger(d)
          : getDynamicLogger(
              d,
              usecase === "newly-uploaded" ? "newlyUploadedData" : "allData"
            ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usecase, statusText, search]);

  useEffect(() => {
    if (search) {
      document.getElementById("Search").addEventListener("search", () => {
        resetLoggerSearch();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleLoadMore = async () => {
    setLoading("loadmore");
    await apiCall({
      type: "get",
      url: `/api/v1/auth/recommendation?limit=${
        datum?.limit * datum?.nextPage
      }${
        usecase && statusText
          ? `&statusText=${statusText}`
          : usecase === "newly-uploaded"
          ? `&streamline=week`
          : ""
      }${search ? `&search=${search}` : ""}`,
      getter: (d: any) =>
        search
          ? getSearchLogger(d)
          : getDynamicLogger(
              d,
              usecase === "newly-uploaded" ? "newlyUploadedData" : "allData"
            ),
    });
    setLoading(false);
  };

  const getStatusColor = (status: Status) => {
    switch (status) {
      case "Pending Acceptance":
        return "text-amber-600";
      case "Accepted":
        return "text-green-600";
      case "Rejected":
        return "text-red-600";
      default:
        return "";
    }
  };

  const [itemOffset, setItemOffset] = useState(0);
  const [range] = useState(10),
    [page, setPage] = useState(1);
  const endOffset = itemOffset + range;

  // if (!datum && !data) return <PageLoader />;
  if (!datum) return;

  const currentItems = datum?.docs?.slice(itemOffset, endOffset);
  const pageCount = Math.ceil(datum?.docs?.length / range);

  const handlePageClick = (event: any) => {
    const newOffset = (event.selected * range) % datum?.docs?.length;
    setItemOffset(newOffset);
    setPage(1 + event?.selected);
  };

  return (
    <>
      <tbody>
        {currentItems?.map((contact: any, i: number) => (
          <tr
            style={{
              background: i % 2 === 0 ? "#F9FAFB" : "transparent",
            }}
            key={contact._id}
            className="h-20"
          >
            <td className="p-3">
              <Checkbox item={contact} datum={datum} setDatum={setDatum} />
            </td>
            <td className="p-3 text-sm text-sec font-medium">
              {contact?.organizationName}
            </td>
            <td className="p-3 text-sm text-sec font-medium hidden">
              {contact.focalPerson}
            </td>
            <td className="p-3 text-sm text-[#667085] font-mediu">
              {contact?.email}
            </td>
            <td className="p-3 text-sm whitespace-nowrap text-[#667085] font-medium hidden">
              {contact?.user?.telephone}
            </td>
            <td className="p-3 font-medium">
              <div
                className={`inline-flex items-center px-5 h-8 rounded-2xl ${
                  contact.statusText === "Rejected"
                    ? "bg-[#FEF3F2]"
                    : contact.statusText === "Accepted"
                    ? "bg-[#ECFDF3]"
                    : "bg-[#FFFAEB]"
                }`}
              >
                <span
                  className={`text-xs rounded-full mr-2 ${
                    contact.statusText === "Rejected"
                      ? "bg-red-500"
                      : contact.statusText === "Acepted"
                      ? "bg-green-500"
                      : "bg-amber-500"
                  }`}
                ></span>
                <span
                  style={{
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                  }}
                  className={getStatusColor(contact.statusText)}
                >
                  {contact.statusText}
                </span>
              </div>
            </td>

            <td className="p-3 text-sm font-medium text-[#667085]">
              {moment(contact?.createdAt)?.format("DD/MM/YY")}
            </td>
            <td className="p-3 flex justify-center items-center h-20 space-x-2 gap-3">
              <div className="relative">
                {/* <button className="text-[#667085] hover:text-gray-700">
									<Trash2 size={18} />
								</button> */}
                <PrimaryBtn
                  // onClick={async e => {
                  // 	e?.stopPropagation();
                  // 	await onSubmit(contact?._id);
                  // }}
                  onClick={async (e) => {
                    e?.stopPropagation();
                    setUpdater({
                      isOpen: "delete",
                      data: [contact],
                      title: "Delete Request",
                    });
                  }}
                  loadingColor="#667085"
                  width="w"
                  text="Delete"
                  className={
                    "text-[#667085] hover:text-gray-700 flex items-center gap-3"
                  }
                  loading={!!isLoading}
                >
                  <Trash2 size={18} />
                </PrimaryBtn>
              </div>
              <div className="relative">
                <button className="text-gray-500 hover:text-gray-700">
                  <View
                    onClick={() =>
                      navigate(`/requests/${contact?.slug || i}`, {
                        state: contact,
                      })
                    }
                    size={18}
                  />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
      <div className="mt-4 flex justify-center">
        <LoadMore
          next={page === pageCount && datum?.hasNextPage}
          loading={loading === "loadmore"}
          handleLoadMore={handleLoadMore}
        />
      </div>
      {pageCount > 1 && (
        <MainPaginate pageCount={pageCount} handlePageClick={handlePageClick} />
      )}
      {updater?.isOpen && (
        <TriggerModal
          title={updater?.title || `${updater?.isOpen?.toUpperCase()} Request`}
          desc={
            updater?.isOpen === "delete"
              ? `Action done cannot be reversible, Proceed to to delete?`
              : `Proceed to update selected request?`
          }
          onClose={() => setUpdater(null)}
          onClick={() => {
            if (updater?.isOpen === "delete") {
              onSubmit();
            }
          }}
          loading={!!isLoading}
        />
      )}
    </>
  );
};
